import React, { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import Box from '@material-ui/core/Box'
import grey from '@material-ui/core/colors/grey'
import { useParams, useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import { VisitLoading } from './VisitLoading'
import { VisitUnavailable } from './VisitUnavailable'
import { useInitAppointment } from './Zoom/utils'
import { SessionProvider } from './Zoom/SessionProvider'
import { Mui5ThemeProvider } from '~/components/providers/ThemeProvider'
import { useSetToken } from '~/utils'
import { Logger } from '~/utils/logging'

/**
 * Entrypoint for web-based video visits
 */
export const Visit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const auth0 = useAuth0()
  const setToken = useSetToken()

  // If unauthenticated, send through redirect flow
  useEffect(() => {
    if (!auth0.isLoading && !auth0.isAuthenticated) {
      auth0.loginWithRedirect({ redirectUri: window.location.origin + '/visit', appState: { id } })
    }
  }, [auth0.isLoading, auth0.isAuthenticated])

  // Fetch appointment and session info
  const init = useInitAppointment(id)

  // If there's an error during init, reroute to error screen
  useEffect(() => {
    if (init.error) history.push('error')
  }, [init.error])

  // Init once authenticated
  useEffect(() => {
    if (auth0.isAuthenticated) {
      setToken.handler().then(() => {
        init.handler()
      })
    }
  }, [auth0.isAuthenticated])

  useEffect(() => {
    if (auth0.isAuthenticated && init.result?.appointment.canJoin && 'user' in init.result)
      Logger.getInstance().setUserContext({
        id: init.result.user.id,
        email: auth0.user?.email,
      })
  }, [init.result, auth0.isAuthenticated, auth0.user])

  // Show loading while fetching appointment info
  if (init.loading || init.error || !init.result) {
    return (
      <Box bgcolor={grey[900]} position="absolute" height="100%" width="100%">
        <Helmet>
          <title>Join Appointment</title>
        </Helmet>
        <VisitLoading />
      </Box>
    )
  }

  const { appointment } = init.result

  // Show unavailable message if the appointment is not within the join window
  // or if user/session are missing (shouldn't happen when canJoin is true)
  if (!appointment.canJoin || !('user' in init.result) || !init.result.session) {
    return <VisitUnavailable />
  }

  // Show the video session (user and session are available when canJoin is true)
  const { user, session } = init.result
  return (
    <Box bgcolor={grey[900]} position="absolute" height="100%" width="100%">
      <Helmet>
        <title>Join Appointment</title>
      </Helmet>
      <Mui5ThemeProvider>
        <SessionProvider
          logger={Logger.getInstance()}
          me={user}
          appointment={appointment}
          sessionName={session.sessionName}
          token={session.token}
          modal={false}
        />
      </Mui5ThemeProvider>
    </Box>
  )
}
